import { useId, useMemo, useState } from "react";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { PickerItem } from "@/pages/PortfolioBuilder/components/builder/ListPicker";
import { formatListName } from "@/shared/utils/formatListName";
import {
  categorizeWatchlist,
  type NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";
import type { Portfolio } from "@/types/Portfolio";
import { relevantCategories } from "../profileMapping";
import { CARD, SECTION_LABEL } from "../ui/theme";
import type { OnboardingAnswers } from "../types";

interface StepWatchlistsProps {
  answers: OnboardingAnswers;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

/**
 * Welcome flow step 2: pick starter watchlists from the Newsquawk prebuilt
 * library, most-relevant categories first. Selections are merged into the
 * primary headline wire as HIGHLIGHTS at apply time — they never filter the
 * feed, so there's no risk of over-narrowing a new user's view.
 *
 * Rows are the watchlist builder's picker rows, so a list looks the same here
 * as where it's combined later: its colour, its name, and a chevron that
 * opens what it holds.
 */
export default function StepWatchlists({
  answers,
  selectedIds,
  onChange,
}: StepWatchlistsProps) {
  const { portfolios, portfoliosQuery } = usePortfoliosCRUD();

  const grouped = useMemo(() => {
    const prebuilt = portfolios.filter(
      (p: Portfolio) => p.owner_type === "NEWSQUAWK"
    );
    const byCategory = new Map<NewsquawkCategory, Portfolio[]>();
    for (const portfolio of prebuilt) {
      const category = categorizeWatchlist(portfolio);
      const list = byCategory.get(category) ?? [];
      list.push(portfolio);
      byCategory.set(category, list);
    }
    // Relevant categories first — then the rest, but only on request.
    //
    // Ordering alone was not enough: someone who picked Energy still had to
    // scroll past Utilities, Macro, Materials, Indices and Technology to see
    // the end of a list they had already told us was irrelevant. The answers
    // from step one are a filter, not a sort hint.
    const preferred = relevantCategories(answers.assetClasses);
    const ordered: { category: NewsquawkCategory; items: Portfolio[] }[] = [];
    for (const category of preferred) {
      const items = byCategory.get(category);
      if (items?.length) {
        ordered.push({ category, items });
        byCategory.delete(category);
      }
    }
    const rest: { category: NewsquawkCategory; items: Portfolio[] }[] = [];
    for (const [category, items] of byCategory) {
      if (items.length) rest.push({ category, items });
    }
    // No answers to filter on (skipped step one) means everything is relevant.
    return ordered.length > 0 ? { ordered, rest } : { ordered: rest, rest: [] };
  }, [portfolios, answers.assetClasses]);

  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const baseId = useId();
  const visible = showAll ? [...grouped.ordered, ...grouped.rest] : grouped.ordered;

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id]
    );
  };

  return (
    <div>
      {portfoliosQuery.isLoading && (
        <p className="text-sm text-gray-500 animate-pulse">Loading the library…</p>
      )}

      {!portfoliosQuery.isLoading && visible.length === 0 && (
        <p className="text-sm text-gray-500">Nothing prebuilt right now — skip this step.</p>
      )}

      <div className="space-y-5">
        {visible.map(({ category, items }) => (
          <div key={category}>
            <h3 className={SECTION_LABEL}>{category}</h3>
            <ul className={`${CARD} m-0 mt-2 list-none overflow-hidden p-0 py-0.5`}>
              {items.map((portfolio) => (
                <PickerItem
                  key={portfolio.id}
                  inputId={`${baseId}-${portfolio.id}`}
                  row={{
                    key: `p-${portfolio.id}`,
                    kind: "watchlist",
                    id: portfolio.id,
                    name: formatListName(portfolio.name),
                    color: portfolio.color,
                    owner: "desk",
                    checked: selectedIds.includes(portfolio.id),
                    contents: {
                      tickers: portfolio.tickers,
                      keywords: portfolio.keywords ?? [],
                      tags: [],
                    },
                  }}
                  expanded={!!expanded[portfolio.id]}
                  onToggle={() => toggle(portfolio.id)}
                  onExpand={() =>
                    setExpanded((prev) => ({ ...prev, [portfolio.id]: !prev[portfolio.id] }))
                  }
                />
              ))}
            </ul>
          </div>
        ))}
      </div>

      {!showAll && grouped.rest.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-5 cursor-pointer border-none bg-transparent p-0 text-sm text-squawk-blue transition-colors hover:text-squawk-blue/80"
        >
          Show the rest of the library
        </button>
      )}
    </div>
  );
}
